import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FiArrowLeft, FiUser, FiShield, FiBriefcase, FiCreditCard, FiTrendingUp,
  FiUsers, FiPercent, FiHelpCircle, FiMail, FiPhone, FiFileText, FiAward,
} from 'react-icons/fi';
import AdminShell from '../../components/AdminShell/AdminShell';
import panelStyles from '../../styles/adminPanels.module.css';
import styles from './AdminUserProfile.module.css';
import axiosClient from '../../api/axiosClient';
import { useCurrency } from '../../app/CurrencyContext';
import FeatureSwitchesPanel from '../../components/FeatureSwitches/FeatureSwitchesPanel';
import {
  AdminKycDocuments, AdminKybDocuments, AdminPersonalForm, AdminKybProfileForm, KybProfileReview, FilledByBadge,
} from './Adminverificationforms';

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api$/, '');
const isImage = (path: string) => /\.(jpe?g|png|gif|webp)$/i.test(path);

const FilePreview: React.FC<{ path: string | null | undefined; label: string }> = ({ path, label }) => {
  if (!path) return null;
  const url = `${API_ORIGIN}${path}`;
  if (isImage(path)) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className={styles.fileThumbLink}>
        <img src={url} alt={label} className={styles.fileThumb} />
        <span>{label}</span>
      </a>
    );
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" className={styles.filePdfLink}>
      <FiFileText size={20} />
      <span>{label} (PDF)</span>
    </a>
  );
};

const STATUS_COLORS: Record<string, string> = {
  pending: '#e8a86a', processing: '#1d60f6', paid: '#5cb87d', approved: '#5cb87d',
  rejected: '#e2534a', active: '#5cb87d', completed: '#1d60f6', open: '#e8a86a', in_progress: '#1d60f6', resolved: '#5cb87d',
};

const StatusPill: React.FC<{ status: string }> = ({ status }) => (
  <span className={panelStyles.statusPill} style={{ color: STATUS_COLORS[status] || '#94a3b8', borderColor: STATUS_COLORS[status] || '#94a3b8' }}>
    {status}
  </span>
);

interface RankInfo {
  currentRank: string | null; selfUsd: number; teamUsd: number; onHold: boolean;
  ranks: { level: number; name: string; rewardUsd: number | null; status: string; awardedAt: string | null; claimedAt: string | null }[];
}

const RankCard: React.FC<{ userId: string }> = ({ userId }) => {
  const [info, setInfo] = useState<RankInfo | null>(null);
  useEffect(() => {
    axiosClient.get(`/admin/users/${userId}/rank`).then(({ data }) => setInfo(data)).catch(() => {});
  }, [userId]);
  if (!info) return null;
  const usd = (n: number) => `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
  const earned = info.ranks.filter((r) => r.status === 'claimable' || r.status === 'claimed');
  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}><FiTrendingUp /><h3>Advisory Board rank</h3></div>
      <div style={{ fontSize: 13, lineHeight: 1.8 }}>
        <div><b>Current rank:</b> {info.currentRank || 'No rank yet'}</div>
        <div><b>Self investment:</b> {usd(info.selfUsd)} &nbsp;·&nbsp; <b>Team business:</b> {usd(info.teamUsd)}</div>
        {info.onHold && <div style={{ color: '#e2534a' }}>No active investment: unclaimed rewards are on hold.</div>}
        {earned.length === 0 && <div style={{ color: 'var(--muted)' }}>No rank reward earned yet.</div>}
        {earned.map((r) => (
          <div key={r.level}>
            {r.level}. {r.name} — {usd(r.rewardUsd ?? 0)} — <b>{r.status === 'claimed' ? 'Claimed' : 'Ready to claim'}</b>
            {r.awardedAt ? ` · earned ${new Date(r.awardedAt).toLocaleDateString()}` : ''}
            {r.claimedAt ? ` · claimed ${new Date(r.claimedAt).toLocaleDateString()}` : ''}
          </div>
        ))}
      </div>
    </div>
  );
};

const MilestoneCard: React.FC<{ userId: string }> = ({ userId }) => {
  const [awards, setAwards] = useState<{ id: string; directName: string; tierLabel: string; pct: number; amountInr: number; status: string; onHold: boolean }[] | null>(null);
  useEffect(() => {
    axiosClient.get(`/admin/users/${userId}/milestones`).then(({ data }) => setAwards(data.awards)).catch(() => {});
  }, [userId]);
  if (!awards) return null;
  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}><FiPercent /><h3>Big investment bonus</h3></div>
      <div style={{ fontSize: 13, lineHeight: 1.8 }}>
        {awards.length === 0 && <div style={{ color: 'var(--muted)' }}>No big investment bonus earned yet.</div>}
        {awards.map((a) => (
          <div key={a.id}>
            {a.directName} reached {a.tierLabel} ({a.pct}%) — ₹{Number(a.amountInr).toLocaleString('en-IN')} — <b>{a.status === 'claimed' ? 'Claimed' : a.onHold ? 'On hold' : 'Ready to claim'}</b>
          </div>
        ))}
      </div>
    </div>
  );
};

const Card: React.FC<{ title: string; icon: React.ReactNode; children: React.ReactNode; empty?: boolean; emptyText?: string }> = ({ title, icon, children, empty, emptyText }) => (
  <div className={styles.card}>
    <div className={styles.cardHeader}>{icon}<h3>{title}</h3></div>
    {empty ? <div className={styles.emptyText}>{emptyText || 'Nothing here yet.'}</div> : children}
  </div>
);

const AdminUserProfile: React.FC = () => {
  const { formatFromInr } = useCurrency();
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [editingPersonal, setEditingPersonal] = useState(false);
  const [editingKyb, setEditingKyb] = useState(false);

  const load = () => {
    if (!id) return;
    axiosClient.get(`/admin/users/${id}`).then(({ data }) => setData(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleStatus = async () => {
    if (!data) return;
    try {
      await axiosClient.patch(`/admin/users/${id}/status`, { isActive: !data.user.is_active });
      toast.success(!data.user.is_active ? 'User activated.' : 'User suspended.');
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not update status.');
    }
  };

  const changeRole = async (role: string) => {
    try {
      await axiosClient.patch(`/admin/users/${id}/role`, { role });
      toast.success(`Role updated to ${role}.`);
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not update role.');
    }
  };

  if (loading || !data) {
    return <AdminShell><div className={panelStyles.page}><div className={panelStyles.muted}>Loading profile…</div></div></AdminShell>;
  }

  const { user, kycPersonal, kyb, kybDocuments, kycDocuments, fundsRequests, investments, portalInvestments, directReferrals, supportTickets, partnerApplications, wallets, rebate } = data;

  return (
    <AdminShell>
      <div className={panelStyles.page}>
        <Link to="/admin/users" className={styles.backLink}><FiArrowLeft /> Back to User Management</Link>

        <div className={styles.profileHeader}>
          <div className={styles.avatar}>{user.full_name?.charAt(0).toUpperCase()}</div>
          <div className={styles.profileInfo}>
            <h1>{user.full_name}</h1>
            <div className={styles.profileMeta}>
              <span><FiMail /> {user.email}</span>
              {user.phone && <span><FiPhone /> {user.phone}</span>}
              <span>Registered {new Date(user.created_at).toLocaleDateString()}</span>
            </div>
          </div>
          <div className={styles.profileActions}>
            <span className={panelStyles.statusPill} style={{ color: user.is_active ? '#5cb87d' : '#e2534a', borderColor: user.is_active ? '#5cb87d' : '#e2534a' }}>
              {user.is_active ? 'Active' : 'Suspended'}
            </span>
            <button className={panelStyles.actionBtn} onClick={toggleStatus}>{user.is_active ? 'Suspend' : 'Activate'}</button>
            <select value={user.role} onChange={(e) => changeRole(e.target.value)} className={styles.roleSelect}>
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>
        </div>

        <div className={styles.grid}>
          <Card title="Basic Info" icon={<FiUser />}>
            <div className={styles.kvList}>
              <div><span>Full Name</span><b>{user.full_name}</b></div>
              <div><span>Email</span><b>{user.email}</b></div>
              <div><span>Phone</span><b>{user.phone || '—'}</b></div>
              <div><span>Fund/Org</span><b>{user.fund_org || '—'}</b></div>
              <div><span>Referral Code</span><b>{user.referral_code || '—'}</b></div>
              <div><span>Referred By</span><b>{user.referred_by_name || '—'}</b></div>
            </div>
          </Card>

          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <FiUser /><h3>Personal Details</h3>
              {!editingPersonal && (
                <button className={styles.editLink} onClick={() => setEditingPersonal(true)}>{kycPersonal ? 'Edit' : 'Fill details'}</button>
              )}
            </div>
            {kycPersonal && (
              <div className={styles.headBadge}>
                <FilledByBadge adminName={kycPersonal.filled_by_admin_name} adminId={kycPersonal.filled_by_admin_id} at={kycPersonal.filled_by_admin_at} />
              </div>
            )}
            {!kycPersonal && !editingPersonal && <div className={styles.emptyText}>Not submitted yet. Use "Fill details" to enter it for this user.</div>}
            {kycPersonal && !editingPersonal && (
              <div className={styles.kvList}>
                <div><span>Name</span><b>{kycPersonal.title} {kycPersonal.first_name} {kycPersonal.middle_name} {kycPersonal.last_name}</b></div>
                <div><span>DOB</span><b>{kycPersonal.dob ? new Date(kycPersonal.dob).toLocaleDateString() : '—'}</b></div>
                <div><span>Gender</span><b>{kycPersonal.gender || '—'}</b></div>
                <div><span>Email</span><b>{kycPersonal.email || '—'}</b></div>
                <div><span>Contact</span><b>{kycPersonal.country_code} {kycPersonal.contact_no}</b></div>
                <div><span>Address</span><b>{kycPersonal.address}, {kycPersonal.city}, {kycPersonal.state}</b></div>
                <div><span>Nationality</span><b>{kycPersonal.nationality || '—'}</b></div>
              </div>
            )}
            {editingPersonal && (
              <AdminPersonalForm
                userId={user.id}
                accountEmail={user.email}
                personal={kycPersonal}
                onClose={() => setEditingPersonal(false)}
                onChanged={load}
              />
            )}
          </div>

          <div className={styles.card}>
            <div className={styles.cardHeader}><FiShield /><h3>KYC Documents</h3></div>
            <AdminKycDocuments userId={user.id} documents={kycDocuments || []} personal={kycPersonal} Preview={FilePreview} onChanged={load} />
          </div>

          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <FiBriefcase /><h3>KYB</h3>
              {!editingKyb && (
                <button className={styles.editLink} onClick={() => setEditingKyb(true)}>{kyb ? 'Edit' : 'Fill company details'}</button>
              )}
            </div>
            {kyb && (
              <div className={styles.headBadge}>
                <FilledByBadge adminName={kyb.filled_by_admin_name} adminId={kyb.filled_by_admin_id} at={kyb.filled_by_admin_at} />
              </div>
            )}
            {!kyb && !editingKyb && <div className={styles.emptyText}>Not submitted yet. Use "Fill company details" to enter it for this user.</div>}
            {kyb && !editingKyb && (
              <>
                <div className={styles.kvList}>
                  <div><span>Status</span><StatusPill status={kyb.status} /></div>
                  <div><span>Company Name</span><b>{kyb.company_name}</b></div>
                  <div><span>Address</span><b>{kyb.company_address}</b></div>
                  <div><span>GST/PAN</span><b>{kyb.gst_pan}</b></div>
                  <div><span>Registration No.</span><b>{kyb.registration_number}</b></div>
                  <div><span>Incorporation Country</span><b>{kyb.incorporation_country}</b></div>
                  <div><span>UBO Name</span><b>{kyb.ubo_name} ({kyb.ubo_designation})</b></div>
                  <div><span>UBO Ownership</span><b>{kyb.ubo_ownership_pct}%</b></div>
                </div>
              </>
            )}
            {editingKyb && (
              <AdminKybProfileForm
                userId={user.id}
                profile={kyb}
                onClose={() => setEditingKyb(false)}
                onChanged={load}
              />
            )}
            {kyb && !editingKyb && <KybProfileReview profile={kyb} onChanged={load} />}
            <AdminKybDocuments userId={user.id} documents={kybDocuments || []} Preview={FilePreview} onChanged={load} />
          </div>

          <div className={styles.card}>
            <div className={styles.cardHeader}><FiShield /><h3>Feature switches</h3></div>
            <FeatureSwitchesPanel mode="user" userId={user.id} />
          </div>

          <RankCard userId={user.id} />
          <MilestoneCard userId={user.id} />

          <Card title="Rebate / Reward" icon={<FiPercent />}>
            <div className={styles.kvList}>
              <div><span>Direct Bonus</span><b>{formatFromInr(rebate.directBonus)}</b></div>
              <div><span>Override Bonus</span><b>{formatFromInr(rebate.overrideBonus)}</b></div>
              <div><span>Total Bonus</span><b>{formatFromInr(rebate.totalBonus)}</b></div>
              <div><span>Claimed</span><b style={{ color: 'var(--success)' }}>{formatFromInr(rebate.claimed)}</b></div>
              <div><span>Claimable</span><b style={{ color: 'var(--warning)' }}>{formatFromInr(rebate.claimable)}</b></div>
            </div>
            {rebate.breakdown && rebate.breakdown.length > 0 && (
              <div className={styles.list} style={{ marginTop: 14 }}>
                {rebate.breakdown.map((row: any, i: number) => (
                  <div key={i} className={styles.listRow}>
                    <div>
                      <b>{row.memberName || 'Unknown'} — {row.type}</b>
                      <div className={styles.listSub}>
                        L{row.level} · Portal {row.portal} · Invested {formatFromInr(Number(row.amount))} · {(row.rate * 100).toFixed(2)}%
                      </div>
                    </div>
                    <b style={{ color: 'var(--copper-light)', whiteSpace: 'nowrap' }}>{formatFromInr(Number(row.bonus))}</b>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card title="Wallet Balance" icon={<FiCreditCard />} empty={wallets.length === 0}>
            <div className={styles.list}>
              {wallets.map((w: any) => (
                <div key={w.currency} className={styles.listRow}>
                  <div>
                    <b>{w.currency}</b>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div>{w.available.toLocaleString()} available</div>
                    {w.held > 0 && <div className={styles.listSub} style={{ color: '#e8a86a' }}>{w.held.toLocaleString()} on hold (Portal principal / 30-day wait)</div>}
                    {w.locked > 0 && <div className={styles.listSub} style={{ color: '#e8a86a' }}>{w.locked.toLocaleString()} locked (pending withdrawal)</div>}
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card title={`Fund Requests (${fundsRequests.length})`} icon={<FiCreditCard />} empty={fundsRequests.length === 0}>
            <div className={styles.list}>
              {fundsRequests.map((f: any) => (
                <div key={f.id} className={styles.listRow}>
                  <div>
                    <b>{f.type === 'deposit' ? 'Deposit' : 'Withdrawal'} — {f.currency !== 'INR' ? `${f.currency} ` : '₹'}{Number(f.amount).toLocaleString()}</b>
                    <div className={styles.listSub}>{f.payment_method_type || 'N/A'} · {f.transaction_ref || f.wallet_address || '—'} · {new Date(f.created_at).toLocaleString()}</div>
                    {f.proof_path && <div className={styles.fileRow} style={{ marginTop: 8 }}><FilePreview path={f.proof_path} label="Payment proof" /></div>}
                  </div>
                  <StatusPill status={f.status} />
                </div>
              ))}
            </div>
          </Card>

          <Card title={`Investments (${investments.length})`} icon={<FiTrendingUp />} empty={investments.length === 0}>
            <div className={styles.list}>
              {investments.map((inv: any) => (
                <div key={inv.id} className={styles.listRow}>
                  <div>
                    <b>{inv.package_name} — ${Number(inv.invest_amount_usd).toLocaleString()}</b>
                    <div className={styles.listSub}>{inv.pay_method} · {new Date(inv.created_at).toLocaleString()}</div>
                  </div>
                  <StatusPill status={inv.status} />
                </div>
              ))}
            </div>
          </Card>

          <Card title={`Portal Investments (${portalInvestments.length})`} icon={<FiTrendingUp />} empty={portalInvestments.length === 0}>
            <div className={styles.list}>
              {portalInvestments.map((inv: any) => (
                <div key={inv.id} className={styles.listRow}>
                  <div>
                    <b>Portal {inv.portal} — {formatFromInr(Number(inv.invest_amount))}</b>
                    <div className={styles.listSub}>
                      {inv.paid_months}/{inv.term_months} months paid ({formatFromInr(Number(inv.paid_amount))}) · Started {new Date(inv.start_date).toLocaleDateString()}
                    </div>
                  </div>
                  <StatusPill status={inv.status} />
                </div>
              ))}
            </div>
          </Card>

          <Card title={`Partner Applications (${partnerApplications.length})`} icon={<FiAward />} empty={partnerApplications.length === 0}>
            <div className={styles.list}>
              {partnerApplications.map((app: any) => (
                <div key={app.id} className={styles.listRow}>
                  <div>
                    <b>{app.tier} Partner</b>
                    <div className={styles.listSub}>Applied {new Date(app.created_at).toLocaleDateString()}</div>
                  </div>
                  <StatusPill status={app.status} />
                </div>
              ))}
            </div>
          </Card>

          <Card title={`Direct Referrals (${directReferrals.length})`} icon={<FiUsers />} empty={directReferrals.length === 0}>
            <div className={styles.list}>
              {directReferrals.map((r: any) => (
                <div key={r.id} className={styles.listRow}>
                  <div>
                    <b>{r.full_name}</b>
                    <div className={styles.listSub}>{r.email} · Joined {new Date(r.created_at).toLocaleDateString()}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card title={`Support Tickets (${supportTickets.length})`} icon={<FiHelpCircle />} empty={supportTickets.length === 0}>
            <div className={styles.list}>
              {supportTickets.map((t: any) => (
                <div key={t.id} className={styles.listRow}>
                  <div>
                    <b>{t.subject}</b>
                    <div className={styles.listSub}>{t.message?.slice(0, 80)}{t.message?.length > 80 ? '…' : ''}</div>
                  </div>
                  <StatusPill status={t.status} />
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </AdminShell>
  );
};

export default AdminUserProfile;